import Card, { CardContent } from '@/components/card';
import DatePickerForm from '@/components/date-picker/DatePickerForm';
import STATE_CODES, { MEXICAN_STATES } from '@/consts/states.constants';
import { useSnackbar } from '@/contexts/SnackBarContext';
import { PlanService } from '@/services/Plan.service';
import SponsorService from '@/services/Sponsor.service';
import formatters from '@/utils/Formatters';
import { LoadingButton } from '@mui/lab';
import {
  Button,
  Divider,
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Radio,
  RadioGroup,
  Select,
  Stack,
  Typography
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import type { Sponsors } from '@vestwell-api/scala';
import { CANADIAN_STATES } from '@vestwell-frontend/helpers';
import {
  BankAccountType,
  CheckMethod,
  DisbursementCountry,
  DisbursementMethod
} from '@vestwell-sub-accounting/models/common/DistributionRequestTypes';

import { AxiosError } from 'axios';
import { Field, useFormikContext } from 'formik';
import { FC, useMemo } from 'react';

import { CreateDisbursementRequestFormValues } from './CreateDisbursementRequestDialog.component';

type CreateDisbursementRequestStep2Props = {
  loading: boolean;
  onBack: () => void;
  planId: string;
};

const mapStatesCodes: Record<DisbursementCountry, string[]> = {
  [DisbursementCountry.USA]: STATE_CODES,
  [DisbursementCountry.Canada]: Object.keys(CANADIAN_STATES),
  [DisbursementCountry.Other]: [],
  [DisbursementCountry.Mexico]: MEXICAN_STATES
};

const countriesDisplayMap: Record<DisbursementCountry, string> = {
  [DisbursementCountry.USA]: 'United States',
  [DisbursementCountry.Canada]: 'Canada',
  [DisbursementCountry.Other]: 'Other',
  [DisbursementCountry.Mexico]: 'Mexico'
};

export const CreateDisbursementRequestStep2: FC<
  CreateDisbursementRequestStep2Props
> = props => {
  const { showSnackbar } = useSnackbar();

  const planQuery = useQuery(
    ['PlanService.getPlanById', props.planId],
    () => PlanService.getPlanById(props.planId),
    {
      enabled: Boolean(props.planId),
      onError: (error: AxiosError) => {
        showSnackbar({
          message: `Error fetching plan: ${error.message}`,
          severity: 'error'
        });
      }
    }
  );

  const sponsorId = useMemo(
    () => planQuery.data?.data?.relationships?.sponsor?.data?.id,
    [planQuery.data?.data?.relationships?.sponsor?.data?.id]
  );

  const bankAccountsQuery = useQuery(
    ['SponsorService.getBankAccounts', sponsorId],
    () => SponsorService.getBankAccounts(sponsorId),
    {
      enabled: !!sponsorId,
      onError: (error: AxiosError) => {
        showSnackbar({
          message: `Error fetching bank accounts: ${error.message}`,
          severity: 'error'
        });
      }
    }
  );

  const formik = useFormikContext<CreateDisbursementRequestFormValues>();

  const questionnaireQuery = useQuery(
    ['SponsorService.getSponsorQuestionnaireInformation', sponsorId],
    () =>
      SponsorService.getSponsorQuestionnaireInformation({
        sponsorId
      }),
    {
      enabled: !!sponsorId && formik.values.step2.payeeType === 'sponsor',
      onError: (error: AxiosError) => {
        showSnackbar({
          message: `Error fetching questionnaire: ${error.message}`,
          severity: 'error'
        });
      },
      onSuccess: data => {
        formik.setValues(prevValues => ({
          ...prevValues,
          step2: {
            ...prevValues.step2,
            payeeAddress: data.address.address1 + ' ' + data.address.address2,
            payeeCity: data.address.city,
            payeeCountry: DisbursementCountry.USA,
            payeeName: data.authSignerFirstName + ' ' + data.authSignerLastName,
            payeeState: data.address.state,
            payeeZip: data.address.zip
          }
        }));
      }
    }
  );

  const fillBankInformation = (
    bankAccount: Sponsors.GetBankAccounts.ResponseBody[number]
  ) => {
    if (
      formik.values.step2.payeeType === 'sponsor' &&
      Boolean(questionnaireQuery.data)
    ) {
      formik.setValues(prevValues => ({
        ...prevValues,
        step2: {
          ...prevValues.step2,
          ABARoutingNumber: bankAccount.routingNumber,
          accountNumber: bankAccount.accountNumber,
          bankName: bankAccount.bankName,
          nameOnAccount: questionnaireQuery.data.companyName
        }
      }));
    }
  };

  return (
    <Stack spacing={2}>
      <Stack direction='row' justifyContent='stretch' spacing={2}>
        <FormControl fullWidth size='small'>
          <InputLabel htmlFor='payee-type'>Payee Type</InputLabel>
          <Select
            data-testid='payee-type'
            id='payee-type'
            label='Payee Type'
            name='step2.payeeType'
            onBlur={formik.handleBlur}
            onChange={formik.handleChange}
            value={formik.values.step2.payeeType}>
            <MenuItem value='other'>Other</MenuItem>
            <MenuItem value='sponsor'>Sponsor</MenuItem>
          </Select>
        </FormControl>
        <FormControl fullWidth size='small'>
          <InputLabel htmlFor='payee-method'>Method</InputLabel>
          <Select
            data-testid='payee-method'
            id='payee-method'
            label='Method'
            name='step2.payeeMethod'
            onBlur={formik.handleBlur}
            onChange={e => {
              if (e.target.value === 'fedex') {
                formik.setFieldValue('step2.checkMethod', CheckMethod.FedEx);
                return;
              } else if (e.target.value === DisbursementMethod.Check) {
                formik.setFieldValue('step2.checkMethod', CheckMethod.Standard);
              }
              formik.handleChange(e);
            }}
            value={formik.values.step2.payeeMethod}>
            {Object.values(DisbursementMethod).map(method => (
              <MenuItem key={`method-${method}`} value={method}>
                {formatters.displayCase(method)}
              </MenuItem>
            ))}
            <MenuItem value='FedEx'>FedEx</MenuItem>
          </Select>
        </FormControl>
      </Stack>
      <Stack spacing={2}>
        {formik.values.step2.payeeType === 'other' ? (
          <>
            <Divider textAlign='left'>Payee Info</Divider>
            <FormControl fullWidth size='small'>
              <InputLabel htmlFor='payee-name'>Payee Name</InputLabel>
              <Field
                as={OutlinedInput}
                error={Boolean(
                  formik.touched.step2?.payeeName &&
                    Boolean(formik.errors.step2?.payeeName)
                )}
                inputProps={{
                  'data-testid': 'payee-name',
                  id: 'payee-name'
                }}
                label='Payee Name'
                name='step2.payeeName'
                onBlur={formik.handleBlur}
                onChange={formik.handleChange}
                value={formik.values.step2.payeeName}
              />
              {formik.touched.step2?.payeeName &&
                Boolean(formik.errors.step2?.payeeName) && (
                  <FormHelperText error>
                    {formik.errors.step2?.payeeName}
                  </FormHelperText>
                )}
            </FormControl>
            {formik.values.step2.payeeMethod ===
              DisbursementMethod.ForceOut && (
              <DatePickerForm
                data-testid='payee-birth-date'
                format='MM/DD/YYYY'
                inputProps={{
                  autoComplete: 'off'
                }}
                label='Date of Birth'
                name='step2.payeeBirthDate'
                size='small'
                value={formik.values.step2.payeeBirthDate}
                variant='outlined'
              />
            )}
            <FormControl fullWidth size='small'>
              <InputLabel htmlFor='payee-address'>Payee Address</InputLabel>
              <Field
                as={OutlinedInput}
                inputProps={{
                  'data-testid': 'payee-address',
                  id: 'payee-address'
                }}
                label='Payee Address'
                name='step2.payeeAddress'
                onBlur={formik.handleBlur}
                onChange={formik.handleChange}
                value={formik.values.step2.payeeAddress}
              />
            </FormControl>
            <FormControl fullWidth size='small'>
              <InputLabel htmlFor='payee-city'>City</InputLabel>
              <Field
                as={OutlinedInput}
                inputProps={{
                  'data-testid': 'payee-city',
                  id: 'payee-city'
                }}
                label='City'
                name='step2.payeeCity'
                onBlur={formik.handleBlur}
                onChange={formik.handleChange}
                value={formik.values.step2.payeeCity}
              />
            </FormControl>
            <FormControl fullWidth size='small'>
              <InputLabel htmlFor='payee-state'>State</InputLabel>
              {formik.values.step2.payeeCountry ===
              DisbursementCountry.Other ? (
                <Field
                  as={OutlinedInput}
                  inputProps={{
                    'data-testid': 'payee-state',
                    id: 'payee-state'
                  }}
                  label='State'
                  name='step2.payeeState'
                  onBlur={formik.handleBlur}
                  onChange={formik.handleChange}
                  value={formik.values.step2.payeeState}
                />
              ) : (
                <Field
                  as={Select}
                  inputProps={{
                    'data-testid': 'state'
                  }}
                  label='State'
                  name='step2.payeeState'
                  onBlur={formik.handleBlur}
                  onChange={formik.handleChange}
                  value={formik.values.step2.payeeState}>
                  {mapStatesCodes[formik.values.step2.payeeCountry].map(
                    stateCode => (
                      <MenuItem key={`state-${stateCode}`} value={stateCode}>
                        {stateCode}
                      </MenuItem>
                    )
                  )}
                </Field>
              )}
            </FormControl>
            <FormControl fullWidth size='small'>
              <InputLabel htmlFor='payee-zip'>Zip</InputLabel>
              <Field
                as={OutlinedInput}
                error={Boolean(
                  formik.touched.step2?.payeeZip &&
                    Boolean(formik.errors.step2?.payeeZip)
                )}
                inputProps={{
                  'data-testid': 'payee-zip',
                  id: 'payee-zip'
                }}
                label='Zip'
                name='step2.payeeZip'
                onBlur={formik.handleBlur}
                onChange={formik.handleChange}
                value={formik.values.step2.payeeZip}
              />
              {formik.touched.step2?.payeeZip &&
                Boolean(formik.errors.step2?.payeeZip) && (
                  <FormHelperText error>
                    {formik.errors.step2?.payeeZip}
                  </FormHelperText>
                )}
            </FormControl>
            <FormControl fullWidth size='small'>
              <InputLabel htmlFor='payee-country'>Country</InputLabel>
              <Field
                as={Select}
                inputProps={{
                  'data-testid': 'payee-country',
                  id: 'payee-country'
                }}
                label='Country'
                name='step2.payeeCountry'
                onBlur={formik.handleBlur}>
                onChange={formik.handleChange}
                value={formik.values.step2.payeeCountry}
                {Object.values(DisbursementCountry).map(country => (
                  <MenuItem key={`country-${country}`} value={country}>
                    {countriesDisplayMap[country]}
                  </MenuItem>
                ))}
              </Field>
            </FormControl>
          </>
        ) : (
          questionnaireQuery.data && (
            <Card>
              <CardContent>
                <Typography fontWeight={700}>
                  {questionnaireQuery.data?.address?.address1}
                </Typography>
                <Typography>
                  {questionnaireQuery.data?.address?.address2}
                </Typography>
                <Typography>{`${questionnaireQuery.data?.address?.city}. ${questionnaireQuery.data?.address?.state}, ${questionnaireQuery.data?.address?.zip}`}</Typography>
              </CardContent>
            </Card>
          )
        )}
      </Stack>
      <Stack spacing={2}>
        <Divider textAlign='left'>Bank Details</Divider>
        <FormControl fullWidth size='small'>
          <InputLabel htmlFor='account-holder-federal-identifier'>
            Account Holder Federal Identifier
          </InputLabel>
          <Field
            as={OutlinedInput}
            error={Boolean(
              formik.touched.step2?.payeeFederalIdentifier &&
                Boolean(formik.errors.step2?.payeeFederalIdentifier)
            )}
            inputProps={{
              'data-testid': 'account-holder-federal-identifier',
              id: 'account-holder-federal-identifier'
            }}
            label='Account Holder Federal Identifier'
            name='step2.payeeFederalIdentifier'
            onBlur={formik.handleBlur}
            onChange={formik.handleChange}
            value={formik.values.step2.payeeFederalIdentifier}
          />
          {formik.touched.step2?.payeeFederalIdentifier &&
            Boolean(formik.errors.step2?.payeeFederalIdentifier) && (
              <FormHelperText error>
                {formik.errors.step2?.payeeFederalIdentifier}
              </FormHelperText>
            )}
        </FormControl>
        {formik.values.step2.payeeMethod !== DisbursementMethod.Check ? (
          <>
            {formik.values.step2.payeeType === 'other' ? (
              <>
                <FormControl>
                  <Stack alignItems='center' direction='row' spacing={2}>
                    <FormLabel data-testid='account-type'>
                      Account Type
                    </FormLabel>
                    <RadioGroup
                      aria-labelledby='demo-radio-buttons-group-label'
                      name='step2.accountType'
                      onBlur={formik.handleBlur}
                      onChange={formik.handleChange}
                      value={formik.values.step2.accountType}>
                      <Stack direction='row' spacing={2}>
                        {Object.values(BankAccountType).map(accountType => (
                          <FormControlLabel
                            control={
                              <Radio
                                data-testid={`account-type-${accountType}-radio`}
                              />
                            }
                            key={`account-type-${accountType}`}
                            label={formatters.displayCase(accountType)}
                            value={accountType}
                          />
                        ))}
                      </Stack>
                    </RadioGroup>
                  </Stack>
                </FormControl>
                <FormControl fullWidth size='small'>
                  <InputLabel htmlFor='bank-name'>Bank Name</InputLabel>
                  <Field
                    as={OutlinedInput}
                    inputProps={{
                      'data-testid': 'bank-name',
                      id: 'bank-name'
                    }}
                    label='Bank Name'
                    name='step2.bankName'
                    onBlur={formik.handleBlur}
                    onChange={formik.handleChange}
                    value={formik.values.step2.bankName}
                  />
                </FormControl>
                <FormControl fullWidth size='small'>
                  <InputLabel htmlFor='name-on-account'>
                    Name on Account
                  </InputLabel>
                  <Field
                    as={OutlinedInput}
                    inputProps={{
                      'data-testid': 'name-on-account',
                      id: 'name-on-account'
                    }}
                    label='Name on Account'
                    name='step2.nameOnAccount'
                    onBlur={formik.handleBlur}
                    onChange={formik.handleChange}
                    value={formik.values.step2.nameOnAccount}
                  />
                </FormControl>
                <FormControl fullWidth size='small'>
                  <InputLabel htmlFor='aba-routing-number'>
                    ABA Routing Number
                  </InputLabel>
                  <Field
                    as={OutlinedInput}
                    error={Boolean(
                      formik.touched.step2?.ABARoutingNumber &&
                        Boolean(formik.errors.step2?.ABARoutingNumber)
                    )}
                    inputProps={{
                      'data-testid': 'aba-routing-number',
                      id: 'aba-routing-number'
                    }}
                    label='ABA Routing Number'
                    name='step2.ABARoutingNumber'
                    onBlur={formik.handleBlur}
                    onChange={formik.handleChange}
                    value={formik.values.step2.ABARoutingNumber}
                  />
                  {formik.touched.step2?.ABARoutingNumber &&
                    Boolean(formik.errors.step2?.ABARoutingNumber) && (
                      <FormHelperText error>
                        {formik.errors.step2?.ABARoutingNumber}
                      </FormHelperText>
                    )}
                </FormControl>
                <FormControl fullWidth size='small'>
                  <InputLabel htmlFor='account-number'>
                    Account Number
                  </InputLabel>
                  <Field
                    as={OutlinedInput}
                    inputProps={{
                      'data-testid': 'account-number',
                      id: 'account-number'
                    }}
                    label='Account Number'
                    name='step2.accountNumber'
                    onBlur={formik.handleBlur}
                    onChange={formik.handleChange}
                    value={formik.values.step2.accountNumber}
                  />
                </FormControl>
                <FormControl fullWidth size='small'>
                  <InputLabel htmlFor='confirm-account-number'>
                    Confirm Account Number
                  </InputLabel>
                  <Field
                    as={OutlinedInput}
                    error={Boolean(
                      formik.touched.step2?.confirmAccountNumber &&
                        Boolean(formik.errors.step2?.confirmAccountNumber)
                    )}
                    inputProps={{
                      'data-testid': 'confirm-account-number',
                      id: 'confirm-account-number'
                    }}
                    label='Confirm Account Number'
                    name='step2.confirmAccountNumber'
                    onBlur={formik.handleBlur}
                    onChange={formik.handleChange}
                    value={formik.values.step2.confirmAccountNumber}
                  />
                  {formik.touched.step2?.confirmAccountNumber &&
                    Boolean(formik.errors.step2?.confirmAccountNumber) && (
                      <FormHelperText error>
                        {formik.errors.step2?.confirmAccountNumber}
                      </FormHelperText>
                    )}
                </FormControl>
              </>
            ) : (
              bankAccountsQuery.data && (
                <FormControl fullWidth size='small'>
                  <InputLabel htmlFor='bank-account'>Bank Account</InputLabel>
                  <Select
                    data-testid='bank-account'
                    id='bank-account'
                    label='Bank account'
                    onChange={e => {
                      fillBankInformation(
                        bankAccountsQuery.data.find(
                          bankAccount =>
                            bankAccount.bankAccountId === e.target.value
                        )!
                      );
                    }}>
                    {bankAccountsQuery.data.map(bankAccount => (
                      <MenuItem
                        key={`bank-account-${bankAccount.bankAccountId}`}
                        value={bankAccount.bankAccountId}>
                        {bankAccount.bankName} ending in{' '}
                        {bankAccount.accountNumber?.slice(-4)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )
            )}
            {formik.values.step2.payeeMethod !== 'wire' ? (
              <FormControl fullWidth size='small'>
                <InputLabel htmlFor='reference-memo'>Reference Memo</InputLabel>
                <Field
                  as={OutlinedInput}
                  inputProps={{
                    'data-testid': 'reference-memo',
                    id: 'reference-memo'
                  }}
                  label='Reference Memo'
                  name='step2.referenceMemo'
                  onBlur={formik.handleBlur}
                  onChange={formik.handleChange}
                  value={formik.values.step2.referenceMemo}
                />
                <FormHelperText>Optional</FormHelperText>
              </FormControl>
            ) : (
              <>
                <FormControl fullWidth size='small'>
                  <InputLabel htmlFor='further-credit-to-name'>
                    Further Credit To Name
                  </InputLabel>
                  <Field
                    as={OutlinedInput}
                    inputProps={{
                      'data-testid': 'further-credit-to-name',
                      id: 'further-credit-to-name'
                    }}
                    label='Further Credit To Name'
                    name='step2.furtherCreditToName'
                    onBlur={formik.handleBlur}
                    onChange={formik.handleChange}
                    value={formik.values.step2.furtherCreditToName}
                  />
                  <FormHelperText>Optional</FormHelperText>
                </FormControl>
                <FormControl fullWidth size='small'>
                  <InputLabel htmlFor='further-credit-to-account-number'>
                    Further Credit To Account Number
                  </InputLabel>
                  <Field
                    as={OutlinedInput}
                    inputProps={{
                      'data-testid': 'further-credit-to-account-number',
                      id: 'further-credit-to-account-number'
                    }}
                    label='Further Credit To Account Number'
                    name='step2.furtherCreditToAccountNumber'
                    onBlur={formik.handleBlur}
                    onChange={formik.handleChange}
                    value={formik.values.step2.furtherCreditToAccountNumber}
                  />
                  <FormHelperText>Optional</FormHelperText>
                </FormControl>
              </>
            )}
          </>
        ) : (
          <FormControl fullWidth size='small'>
            <InputLabel htmlFor='overnight-account-number'>
              Overnight Account Number
            </InputLabel>
            <Field
              as={OutlinedInput}
              inputProps={{
                'data-testid': 'overnight-account-number',
                id: 'overnight-account-number'
              }}
              label='Overnight Account Number'
              name='step2.overnightAccountNumber'
              onBlur={formik.handleBlur}
              onChange={formik.handleChange}
              value={formik.values.step2.overnightAccountNumber}
            />
          </FormControl>
        )}
        <Stack direction='row' spacing={2}>
          <LoadingButton
            disabled={!formik.isValid}
            loading={props.loading}
            type='submit'
            variant='contained'>
            Submit for approval
          </LoadingButton>
          <Button onClick={props.onBack}>Back</Button>
        </Stack>
      </Stack>
    </Stack>
  );
};
